import React, { useState } from 'react'
import { X, AlertTriangle, ShieldAlert } from 'lucide-react'
import { colors, radii, typography } from '../../styles/tokens'
import { qaApi, type ReportPayload } from '../../api/qa'
import toast from 'react-hot-toast'

interface ReportModalProps {
  isOpen: boolean
  onClose: () => void
  targetType: 'question' | 'answer'
  targetId: string
  targetTitle?: string
}

const REPORT_REASONS = [
  'Inaccurate or misleading information',
  'Spam, advertising, or promotional content',
  'Harassment, hate speech, or abuse',
  'Off-topic or irrelevant discussion',
  'Impersonation or unauthorized credentials',
  'Other violation of community guidelines',
]

export function ReportModal({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetTitle,
}: ReportModalProps) {
  const [reason, setReason] = useState(REPORT_REASONS[0])
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    try {
      await qaApi.report({
        target_type: targetType,
        target_id: targetId,
        reason,
        details: details.trim(),
      })
      toast.success('Report submitted. Thank you for keeping Campnect trustworthy.')
      onClose()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to submit report. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          background: 'var(--color-bg)',
          borderRadius: 20,
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-float)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldAlert size={18} style={{ color: '#EF4444' }} />
            <h3
              style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontFamily: typography.fontDisplay,
              }}
            >
              Report {targetType === 'question' ? 'Question' : 'Answer'}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              padding: '4px',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content form */}
        <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {targetTitle && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: radii.md,
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                fontSize: '13px',
                color: 'var(--color-text-secondary)',
              }}
            >
              <strong>Content:</strong> {targetTitle}
            </div>
          )}

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                marginBottom: '6px',
              }}
            >
              Reason for reporting
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {REPORT_REASONS.map((r) => (
                <label
                  key={r}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    borderRadius: radii.md,
                    border: '1px solid',
                    borderColor: reason === r ? '#111114' : 'var(--color-border)',
                    background: reason === r ? 'var(--color-surface)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  <input
                    type="radio"
                    name="reportReason"
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    style={{ accentColor: '#111114' }}
                  />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                marginBottom: '6px',
              }}
            >
              Additional context (optional)
            </label>
            <textarea
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe why this content violates community guidelines..."
              maxLength={1000}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: radii.md,
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                fontSize: '13px',
                resize: 'none',
                fontFamily: 'inherit',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: radii.pill,
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--color-text-primary)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '8px 20px',
                borderRadius: radii.pill,
                border: 'none',
                background: '#EF4444',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: typography.fontDisplay,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
